/**
 * DBox Update Dialog for Master Database
 * - Preview and apply updates from DBox API
 */
import { Loader2, RefreshCw, Database, Trophy, AlertCircle, ArrowRight, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * DBox Update Dialog
 */
export function DBoxUpdateDialog({
  open,
  onOpenChange,
  selectedGame,
  previewLoading,
  previewData,
  selectedFields,
  onToggleField,
  applyAchievements,
  onApplyAchievementsChange,
  uploadToCloudinary,
  onUploadToCloudinaryChange,
  exophaseHtml,
  onExophaseHtmlChange,
  applyExophaseScores,
  onApplyExophaseScoresChange,
  customBoxArtUrl,
  onCustomBoxArtUrlChange,
  updateLoading,
  onUpdate,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-green-400" />
            Update from DBox
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {selectedGame?.name} • Stored ID: {selectedGame?.xboxTitleId}
            {previewData && (
              <span className="text-green-400 ml-2">
                → DBox Hex: {previewData.title_id}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        {previewLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-400" />
            <span className="ml-3 text-white/60">Fetching data from DBox...</span>
          </div>
        ) : previewData ? (
          <div className="space-y-4 pt-4">
            {/* Field updates */}
            <div className="space-y-3">
              <h4 className="font-medium text-white flex items-center gap-2">
                <Database className="w-4 h-4" />
                Game Information
              </h4>
              
              {[
                { key: "developer", label: "Developer" },
                { key: "publisher", label: "Publisher" },
                { key: "releaseDate", label: "Release Date" },
                { key: "description", label: "Description" },
              ].map(({ key, label }) => {
                const currentVal = previewData.current_values[key];
                const newVal = previewData.new_values[key];
                const hasConflict = previewData.conflicts.includes(key);
                const isMissing = previewData.missing_in_dbox.includes(key);
                const isSelected = selectedFields.includes(key);
                
                return (
                  <div 
                    key={key}
                    className={`p-3 rounded-lg border ${
                      isMissing 
                        ? "border-yellow-500/30 bg-yellow-500/5" 
                        : hasConflict 
                          ? "border-orange-500/30 bg-orange-500/5" 
                          : newVal 
                            ? "border-green-500/30 bg-green-500/5" 
                            : "border-white/10 bg-white/5"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{label}</span>
                          {isMissing && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                              Not in DBox
                            </span>
                          )}
                          {hasConflict && !isMissing && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                              Will overwrite
                            </span>
                          )}
                          {newVal && !currentVal && !isMissing && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">
                              New data
                            </span>
                          )}
                        </div>
                        
                        {key === "description" ? (
                          <div className="space-y-1">
                            {currentVal && (
                              <p className="text-xs text-white/50 line-clamp-2">
                                <span className="text-white/30">Current: </span>{currentVal}
                              </p>
                            )}
                            {newVal && (
                              <p className="text-xs text-green-400/80 line-clamp-2">
                                <span className="text-green-400/50">DBox: </span>{newVal}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-sm">
                            {currentVal && (
                              <span className="text-white/50">{currentVal}</span>
                            )}
                            {currentVal && newVal && newVal !== currentVal && (
                              <ArrowRight className="w-3 h-3 text-white/30" />
                            )}
                            {newVal && newVal !== currentVal && (
                              <span className="text-green-400">{newVal}</span>
                            )}
                            {!currentVal && !newVal && (
                              <span className="text-white/30 italic">No data</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {newVal && !isMissing && (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => onToggleField(key)}
                          className="mt-1"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Box Art - Manual URL Input */}
              <div className={`p-3 rounded-lg border ${
                customBoxArtUrl 
                  ? "border-green-500/30 bg-green-500/5"
                  : "border-purple-500/30 bg-purple-500/5"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-4 h-4 text-purple-400" />
                  <span className="font-medium text-sm">Box Art</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                    Paste Cloudinary URL
                  </span>
                </div>
                
                <div className="flex gap-3 items-start">
                  {previewData.current_values.boxArtUrl && (
                    <div className="text-center flex-shrink-0">
                      <p className="text-[10px] text-white/40 mb-1">Current</p>
                      <img 
                        src={previewData.current_values.boxArtUrl} 
                        alt="Current" 
                        className="w-16 h-20 object-contain bg-black/50 rounded"
                      />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <Input
                      value={customBoxArtUrl}
                      onChange={(e) => onCustomBoxArtUrlChange(e.target.value)}
                      placeholder="Paste Cloudinary URL for box art..."
                      className="bg-black/50 border-white/10 text-sm"
                      data-testid="dbox-custom-boxart-input"
                    />
                    <p className="text-[10px] text-white/40 mt-1">
                      Upload image to Cloudinary first, then paste the URL here
                    </p>
                    {customBoxArtUrl && (
                      <div className="mt-2">
                        <p className="text-[10px] text-white/40 mb-1">Preview</p>
                        <img 
                          src={customBoxArtUrl} 
                          alt="New box art" 
                          className="w-16 h-20 object-contain bg-black/50 rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Achievements section */}
            <div className="space-y-3 pt-2 border-t border-white/10">
              <h4 className="font-medium text-white flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                Achievements
              </h4>
              
              <div className={`p-3 rounded-lg border ${
                previewData.achievements_preview.dbox_count > 0 
                  ? "border-green-500/30 bg-green-500/5" 
                  : "border-yellow-500/30 bg-yellow-500/5"
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">
                      <span className="text-white/50">Current: </span>
                      <span className="text-white">{previewData.achievements_preview.existing_count} achievements</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-white/50">DBox: </span>
                      <span className="text-green-400">
                        {previewData.achievements_preview.dbox_count} achievements 
                        ({previewData.achievements_preview.dbox_total_gamerscore} GS)
                      </span>
                      {previewData.achievements_preview.format && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                          {previewData.achievements_preview.format}
                        </span>
                      )}
                      {previewData.achievements_preview.has_images && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                          Has images
                        </span>
                      )}
                    </p>
                  </div>
                  
                  {previewData.achievements_preview.dbox_count > 0 && (
                    <Checkbox
                      checked={applyAchievements}
                      onCheckedChange={onApplyAchievementsChange}
                    />
                  )}
                </div>
                
                {previewData.achievements_preview.sample_achievements?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <p className="text-[10px] text-white/40 mb-1">Sample achievements:</p>
                    <div className="flex flex-wrap gap-1">
                      {previewData.achievements_preview.sample_achievements.map((a, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded bg-white/10 text-white/70">
                          {a.name} ({a.gamerscore}G)
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Exophase HTML paste for achievement images */}
                {applyAchievements && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <Label className="text-xs text-white/70 mb-2 block">
                      <ImageIcon className="w-3 h-3 inline mr-1" />
                      Paste Exophase Page Source for Achievement Images (Optional)
                    </Label>
                    <p className="text-[10px] text-white/40 mb-2">
                      DBox doesn't provide achievement images. To add images, go to the Exophase achievements page, 
                      right-click → "View Page Source", copy all, and paste below.
                    </p>
                    <textarea
                      value={exophaseHtml}
                      onChange={(e) => onExophaseHtmlChange(e.target.value)}
                      placeholder="Paste Exophase page source HTML here..."
                      className="w-full h-24 p-2 text-xs bg-black/50 border border-white/10 rounded text-white/70 font-mono resize-none"
                      data-testid="dbox-exophase-html-input"
                    />
                    {exophaseHtml && exophaseHtml.length > 100 && (
                      <p className="text-[10px] text-green-400 mt-1">
                        ✓ HTML pasted ({Math.round(exophaseHtml.length / 1024)}KB) - will match achievement images by name
                      </p>
                    )}

                    {/* Apply Exophase scores — opt-in override of GS/XP
                        when porting a game between platforms. */}
                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                      <Checkbox
                        checked={!!applyExophaseScores}
                        onCheckedChange={onApplyExophaseScoresChange}
                        data-testid="dbox-apply-exophase-scores"
                        disabled={!exophaseHtml || exophaseHtml.length < 100}
                      />
                      <div className="flex-1">
                        <div className="text-xs text-white/80">
                          Also update XP / Gamerscore from Exophase
                        </div>
                        <p className="text-[10px] text-white/40 mt-0.5">
                          Replaces each achievement's score with the value parsed from the pasted HTML.
                          Useful when copying a game between platforms with different point values
                          (PSN XP ↔ Xbox GS).
                        </p>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>
            
            {/* Options */}
            <div className="flex items-center gap-3 pt-2 border-t border-white/10">
              <Checkbox
                id="cloudinary-upload"
                checked={uploadToCloudinary}
                onCheckedChange={onUploadToCloudinaryChange}
              />
              <Label htmlFor="cloudinary-upload" className="text-sm text-white/70 cursor-pointer">
                Upload images to Cloudinary
              </Label>
            </div>
            
            {/* Warning for missing data */}
            {previewData.missing_in_dbox.length > 2 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-xs text-yellow-300">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  DBox has limited data for this game. It will be flagged for review after update.
                </p>
              </div>
            )}
          </div>
        ) : null}
        
        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/20"
          >
            Cancel
          </Button>
          <Button
            onClick={onUpdate}
            disabled={updateLoading || (!selectedFields.length && !applyAchievements && !customBoxArtUrl)}
            className="bg-green-600 hover:bg-green-500"
            data-testid="dbox-update-confirm-btn"
          >
            {updateLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Apply Updates
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
